import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getApiUrl } from '../config/api';
import { isDesignSystemOn } from '../theme/mmecDsPreview';


const LoginScreen: React.FC = () => {
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const { loginWithToken } = useAuth();
  const { language } = useLanguage();

  const isArabic = language === 'ar';

  // Android: a token link can open the MMEC Terminal app instead of the web
  // session — offer that before auto-logging into the browser.
  const [mobileOfferToken, setMobileOfferToken] = useState<string | null>(null);
  const [mobileCopied, setMobileCopied] = useState(false);

  // Auto-detect token from URL on component mount
  useEffect(() => {
    console.log('🔍 LoginScreen: Checking for tokens in URL...');

    // Check for token in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');

    if (urlToken && /Android/i.test(navigator.userAgent)) {
      setToken(urlToken);
      setMobileOfferToken(urlToken); // hold web login until the visitor picks
      return;
    }

    if (urlToken) {
      console.log('🔍 LoginScreen: Token found in URL parameters, auto-login...');
      setToken(urlToken);
      handleTokenLogin(urlToken);
    } else {
      // Check if we're on a final-bypass.php URL
      const currentPath = window.location.pathname;
      if (currentPath.includes('final-bypass.php')) {
        console.log('🔍 LoginScreen: Final-bypass.php route detected, checking for token...');
        const bypassToken = urlParams.get('token');
        if (bypassToken) {
          console.log('🔍 LoginScreen: Token found in final-bypass.php, auto-login...');
          setToken(bypassToken);
          handleTokenLogin(bypassToken);
        }
      }
    }
  }, []);

  const handleTokenLogin = async (tokenToUse: string) => {
    try {
      setLoading(true);
      setError('');

      // Extract token from various URL formats
      let extractedToken = tokenToUse;
      
      // Handle full URLs with token parameters
      if (tokenToUse.includes('http') && tokenToUse.includes('token=')) {
        try {
          const url = new URL(tokenToUse);
          extractedToken = url.searchParams.get('token') || tokenToUse;
        } catch (e) {
          // If URL parsing fails, try regex extraction
          const tokenMatch = tokenToUse.match(/token=([^&]+)/);
          if (tokenMatch) {
            extractedToken = tokenMatch[1];
          }
        }
      }
      
      // Handle URLs with hash tokens
      if (tokenToUse.includes('#')) {
        const hashToken = tokenToUse.split('#')[1];
        if (hashToken.startsWith('token=')) {
          extractedToken = hashToken.replace('token=', '');
        }
      }
      
      // Handle path-based tokens (e.g., /token/abc123)
      if (tokenToUse.includes('/token/')) {
        const pathMatch = tokenToUse.match(/\/token\/([^\/\?]+)/);
        if (pathMatch) {
          extractedToken = pathMatch[1];
        }
      }

      console.log('Attempting token login...');
      
      const response = await fetch(getApiUrl('/api/auth/verify-flexible'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token: extractedToken })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('🔍 LoginScreen: Backend response:', result);
        
        // Check for successful authentication using the actual backend response format
        if (result.valid === true || result.status === 'success') {
          console.log('✅ Token is valid, logging in...');
          
          // Login with token
          const loginSuccess = await loginWithToken(extractedToken);
          
          if (loginSuccess) {
            setSuccess('Login successful! Redirecting...');
            
            // Clear the token from URL
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
            
            // Redirect to dashboard after 2 seconds
            setTimeout(() => {
              window.location.href = '/dashboard';
            }, 2000);
          } else {
            setError('Login failed. Please try again.');
          }
        } else {
          console.error('❌ Token verification failed:', result);
          setError('Invalid token. Please check your login URL.');
        }
      } else {
        setError('Token verification failed. Please try again.');
      }
    } catch (error) {
      console.error('Token login error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Design System preview (artboard web/sign-in): presentation only; the token flow above is untouched.
  const ds = isDesignSystemOn();
  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    background: 'var(--ground, var(--base))',
    color: 'var(--txt)',
    fontFamily: ds && isArabic ? 'var(--font-arabic)' : 'var(--font-ui)',
    position: ds ? 'relative' : undefined,
  };
  const pageDir = ds ? (isArabic ? 'rtl' : 'ltr') : undefined;
  const cardStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: ds ? 400 : 380,
    boxSizing: 'border-box',
    padding: ds ? 24 : 28,
    background: 'var(--surface-panel, var(--elev))',
    border: '1px solid var(--hair)',
    borderRadius: 'var(--radius-md, 12px)',
    boxShadow: 'var(--shadow-panel, var(--lift-2))',
  };
  const noticeStyle = (tone: 'down' | 'up'): React.CSSProperties => ({
    marginBottom: 12,
    padding: '8px 12px',
    borderRadius: ds ? 4 : 8,
    fontSize: 12.5,
    lineHeight: 1.45,
    background: `var(--${tone}-dim)`,
    color: `var(--${tone})`,
    border: `1px solid color-mix(in srgb, var(--${tone}) 40%, transparent)`,
  });

  if (mobileOfferToken) {
    const deepLink = `mmec://login?mmectoken=${encodeURIComponent(mobileOfferToken)}`;
    const copyMobileToken = async () => {
      try {
        await navigator.clipboard.writeText(mobileOfferToken);
        setMobileCopied(true);
        window.setTimeout(() => setMobileCopied(false), 2000);
      } catch {
        /* clipboard unavailable — token is still in the login field */
      }
    };
    const btnBase: React.CSSProperties = {
      display: 'block',
      width: '100%',
      padding: '12px 16px',
      borderRadius: ds ? 4 : 10,
      fontWeight: 600,
      fontSize: 14,
      marginBottom: 12,
      textAlign: 'center' as const,
      cursor: 'pointer',
    };
    return (
      <div style={pageStyle} dir={pageDir}>
        <div style={{ maxWidth: 400, width: '100%', padding: 24, textAlign: 'center' }}>
          <h2 style={{ margin: '0 0 4px', fontSize: 19, color: 'var(--txt)' }}>
            {isArabic ? 'فتح في تطبيق MMEC Terminal؟' : 'Open in the MMEC Terminal app?'}
          </h2>
          <p style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--muted)' }}>
            {isArabic
              ? 'رابط الدخول الخاص بك يعمل في التطبيق وفي المتصفح.'
              : 'Your login link works in the app and in the browser.'}
          </p>
          <a href={deepLink} style={{ ...btnBase, background: 'var(--gold, var(--acc))', color: 'var(--text-on-accent, #04121f)', textDecoration: 'none' }}>
            {isArabic ? 'فتح في تطبيق MMEC Terminal' : 'Open in MMEC Terminal'}
          </a>
          <button
            onClick={copyMobileToken}
            style={{ ...btnBase, background: 'transparent', color: 'var(--txt)', border: '1px solid var(--border)' }}
          >
            {mobileCopied ? (isArabic ? 'تم النسخ ✓' : 'Copied ✓') : (isArabic ? 'نسخ الرمز' : 'Copy token')}
          </button>
          <button
            onClick={() => {
              const t = mobileOfferToken;
              setMobileOfferToken(null);
              handleTokenLogin(t);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--muted)',
              fontSize: 13,
              textDecoration: 'underline',
              cursor: 'pointer',
              padding: 8,
            }}
          >
            {isArabic ? 'المتابعة في المتصفح' : 'Continue in browser'}
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={pageStyle} dir={pageDir}>
        <div style={{ textAlign: 'center' }}>
          <div
            className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4"
            style={{ borderBottomColor: 'var(--gold, var(--acc))' }}
          ></div>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--muted)' }}>{isArabic ? 'جاري التحميل...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle} dir={pageDir}>
      {ds ? (
        <>
          <div style={{ position: 'absolute', insetInlineStart: 24, top: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src="/logo.png" alt="" style={{ height: 20, width: 20 }} />
            <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: isArabic ? 0 : '.04em' }}>MMEC</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>RTL Market</span>
          </div>
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 24, textAlign: 'center', fontSize: 11, color: 'var(--text-muted)' }}>
            Modern Money Experts · stocks.modernmoneyexperts.com
          </div>
        </>
      ) : null}
      <div style={cardStyle}>
        {/* MMEC Logo */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img
            src="/logo.png"
            alt="MMEC Logo"
            style={{ display: 'block', height: 48, width: 48, margin: '0 auto 12px' }}
          />
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--txt)' }}>
            {isArabic ? 'MME Analytics Platform' : 'MME Analytics Platform'}
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--muted)' }}>
            {isArabic ? 'منصة التحليل المتقدم للأسواق المالية' : 'Advanced Market Intelligence Platform'}
          </p>
        </div>

        {/* Instructions */}
        <div
          style={{
            marginBottom: 16,
            padding: '10px 12px',
            background: 'var(--elev2)',
            border: '1px solid var(--hair)',
            borderRadius: 8,
          }}
        >
          <h3
            style={{
              margin: '0 0 6px',
              fontSize: 11,
              fontWeight: 600,
              textTransform: ds && isArabic ? 'none' : 'uppercase',
              letterSpacing: ds && isArabic ? 0 : '.08em',
              color: 'var(--text-muted, var(--muted-2))',
            }}
          >
            {isArabic ? 'طرق الدخول المتاحة:' : 'Available Login Methods:'}
          </h3>
          <ul
            style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontSize: 12,
              lineHeight: 1.45,
              color: 'var(--txt-2)',
            }}
          >
            <li>• {isArabic ? 'رابط مباشر مع التوكن' : 'Direct link with token'}</li>
            <li>• {isArabic ? 'نسخ ولصق التوكن' : 'Copy & paste token'}</li>
            <li>• {isArabic ? 'رابط من صفحة تسجيل الدخول' : 'Link from login page'}</li>
          </ul>
        </div>

        {/* Quick Access Button */}
        <div style={{ marginBottom: 16 }}>
          <button
            onClick={() => window.open('https://stocks.modernmoneyexperts.com/login-2/', '_blank')}
            className="mmec-btn"
            style={{ width: '100%', height: ds ? 30 : 32, fontSize: 12.5 }}
          >
            {isArabic ? 'الحصول على رابط الدخول' : 'Get Login Link'}
          </button>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div style={noticeStyle('down')}>
            {error}
          </div>
        )}
        {success && (
          <div style={noticeStyle('up')}>
            {success}
          </div>
        )}

        {/* Token Login Form */}
        <div>
          <form
            onSubmit={(e) => { e.preventDefault(); handleTokenLogin(token); }}
            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <div>
              <label
                htmlFor="token"
                style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 500, color: 'var(--txt-2)' }}
              >
                {isArabic ? 'رمز الدخول أو الرابط' : 'Login Token or URL'}
              </label>
              <textarea
                id="token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder={isArabic 
                  ? 'أدخل رمز الدخول أو الرابط الكامل هنا...' 
                  : 'Enter your login token or full URL here...'
                }
                rows={3}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '8px 10px',
                  background: 'var(--surface-2, var(--base))',
                  color: 'var(--txt)',
                  border: '1px solid var(--line-strong, var(--border))',
                  borderRadius: ds ? 4 : 8,
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12.5,
                  lineHeight: 1.45,
                  resize: 'none',
                }}
                required
              />
              <p style={{ margin: '6px 0 0', fontSize: 11.5, color: 'var(--muted)' }}>
                {isArabic 
                  ? 'يمكنك لصق الرابط الكامل أو رمز الدخول فقط' 
                  : 'You can paste the full URL or just the token'
                }
              </p>
            </div>
            
            <button
              type="submit"
              disabled={loading || !token.trim()}
              style={{
                width: '100%',
                height: ds ? 44 : 32,
                border: 'none',
                borderRadius: ds ? 4 : 8,
                background: 'var(--gold, var(--acc))',
                color: 'var(--text-on-accent, var(--void))',
                fontFamily: 'var(--font-ui)',
                fontSize: ds ? 14 : 13,
                fontWeight: 600,
                cursor: loading || !token.trim() ? 'not-allowed' : 'pointer',
                opacity: loading || !token.trim() ? 0.5 : 1,
                transition: 'opacity var(--dur-micro) var(--ease)',
              }}
            >
              {loading 
                ? (isArabic ? '⏳ جاري تسجيل الدخول...' : '⏳ Logging in...') 
                : (isArabic ? 'تسجيل الدخول' : 'Login')
              }
            </button>
          </form>
        </div>

        <div style={{ marginTop: 20, textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--muted)' }}>
            {isArabic 
              ? 'انقر على الزر أعلاه للحصول على رابط الدخول المباشر' 
              : 'Click the button above to get your direct login link'
            }
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--muted-2)' }}>
            {isArabic 
              ? 'أو استخدم الرابط المباشر مع التوكن: /?token=YOUR_TOKEN' 
              : 'Or use direct link: /?token=YOUR_TOKEN'
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen; 